import {
  Calendar,
  ChevronDown,
  DollarSign,
  Eye,
  Globe,
  MousePointer,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsGetPlacementDetails,
  type GoogleAdsGetPlacementDetailsResult,
} from 'sprites-types/workflow/tools/googleAdsGetPlacementDetails'
import { ObjectDisplay } from '../shared/ObjectDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

type PlacementPerformanceItem = GoogleAdsGetPlacementDetailsResult[number]

// Utility functions
const formatCurrency = (amount: number | null | undefined) => {
  if (amount == null) return 'N/A'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const formatNumber = (num: number | null | undefined) => {
  if (num == null) return 'N/A'
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const getPlacementTypeColor = (type: string | null | undefined) => {
  switch (type) {
    case 'SEARCH':
      return 'bg-blue-100 text-blue-800'
    case 'DISPLAY':
      return 'bg-purple-100 text-purple-800'
    case 'YOUTUBE':
      return 'bg-red-100 text-red-800'
    case 'GMAIL':
      return 'bg-green-100 text-green-800'
    case 'DISCOVER':
      return 'bg-indigo-100 text-indigo-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function PlacementItem({ placement }: { placement: PlacementPerformanceItem }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const perf = placement.performance || {}

  // Only show expand button if there's data to show
  const hasExpandableContent =
    (placement.dailyBreakdown && placement.dailyBreakdown.length > 0) || placement.campaignId

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 p-5 shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100">
              <Globe className="h-4 w-4 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900">
                {placement.placement || 'Unknown Placement'}
              </h4>
              {placement.campaignName && (
                <p className="mt-0.5 text-xs text-gray-500">Campaign: {placement.campaignName}</p>
              )}
            </div>
            {placement.placementType && placement.placementType !== 'UNKNOWN' && (
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${getPlacementTypeColor(placement.placementType)}`}
              >
                {placement.placementType}
              </span>
            )}
          </div>
        </div>
        {hasExpandableContent && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-3 text-gray-400 transition-colors hover:text-gray-600"
          >
            <ChevronDown
              className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {/* Performance Metrics - Prominent Impressions */}
      <div className="mt-4">
        <div className="rounded-xl border border-gray-200/50 bg-gradient-to-br from-indigo-50 to-blue-50 p-4">
          <div className="mb-1.5 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100">
              <Eye className="h-4 w-4 text-indigo-600" />
            </div>
            <span className="text-xs font-medium text-gray-700">Impressions</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatNumber(perf.impressions)}</div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && hasExpandableContent && (
        <div className="mt-4 border-t border-gray-200/50 pt-4">
          {/* Campaign ID */}
          {placement.campaignId && (
            <div className="mb-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-blue-100">
                  <Target className="h-3.5 w-3.5 text-blue-600" />
                </div>
                <span className="text-xs font-semibold text-gray-700">Campaign Details</span>
              </div>
              <div className="rounded-lg border border-gray-200/50 bg-gray-50/50 p-2.5">
                <div className="text-xs font-medium text-gray-600">Campaign ID</div>
                <div className="mt-0.5 font-mono text-sm font-semibold text-gray-900">
                  {placement.campaignId}
                </div>
              </div>
            </div>
          )}

          {/* Daily Breakdown */}
          {placement.dailyBreakdown && placement.dailyBreakdown.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-indigo-100">
                  <Calendar className="h-3.5 w-3.5 text-indigo-600" />
                </div>
                <span className="text-xs font-semibold text-gray-700">Daily Performance</span>
              </div>
              <div className="space-y-2">
                {placement.dailyBreakdown.slice(-7).map((day, index) => (
                  <div
                    key={day.date || index}
                    className="flex items-center justify-between rounded-lg border border-gray-200/50 bg-gray-50/50 px-3 py-2"
                  >
                    <span className="text-xs font-medium text-gray-700">{day.date}</span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-semibold text-gray-900">
                        {formatNumber(day.impressions)} imp
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function GoogleAdsGetPlacementDetailsDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const [showJson, setShowJson] = useState(false)
  const schema = getToolResultSchema(googleAdsGetPlacementDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsGetPlacementDetailsResult

  if (showJson) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Placement Details Results</h3>
          <button
            onClick={() => setShowJson(false)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            View Formatted
          </button>
        </div>
        <ObjectDisplay data={result} />
      </div>
    )
  }

  if (!result || result.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Placement Details Results</h3>
        <p className="mt-2 text-sm text-gray-600">No placement performance data found.</p>
      </div>
    )
  }

  // Calculate totals
  const totals = result.reduce(
    (acc, placement) => {
      const perf = placement.performance || {}
      acc.impressions += perf.impressions || 0
      acc.clicks += perf.clicks || 0
      acc.cost += perf.cost || 0
      acc.cpm += perf.cpm || 0
      return acc
    },
    { impressions: 0, clicks: 0, cost: 0, cpm: 0 }
  )

  const avgCPM = totals.impressions > 0 ? (totals.cost / totals.impressions) * 1000 : 0

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Placement Details Results</h3>
          <p className="mt-0.5 text-xs text-gray-600">
            {result.length} placement{result.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowJson(true)}
          className="text-xs text-blue-600 transition-colors hover:text-blue-800"
        >
          View JSON
        </button>
      </div>

      {/* Summary Stats - Block Style */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-gray-200/50 bg-gradient-to-br from-indigo-50 to-blue-50 p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100">
              <Eye className="h-4 w-4 text-indigo-600" />
            </div>
            <span className="text-xs font-medium text-gray-700">Total Impressions</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatNumber(totals.impressions)}</div>
        </div>
        {totals.clicks > 0 && (
          <div className="rounded-2xl border border-gray-200/50 bg-gradient-to-br from-green-50 to-emerald-50 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100">
                <MousePointer className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-xs font-medium text-gray-700">Total Clicks</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(totals.clicks)}</div>
          </div>
        )}
        {totals.cost > 0 && (
          <div className="rounded-2xl border border-gray-200/50 bg-gradient-to-br from-yellow-50 to-amber-50 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-100">
                <DollarSign className="h-4 w-4 text-yellow-600" />
              </div>
              <span className="text-xs font-medium text-gray-700">Total Cost</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(totals.cost)}</div>
          </div>
        )}
        {avgCPM > 0 && (
          <div className="rounded-2xl border border-gray-200/50 bg-gradient-to-br from-purple-50 to-pink-50 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100">
                <TrendingUp className="h-4 w-4 text-purple-600" />
              </div>
              <span className="text-xs font-medium text-gray-700">Avg CPM</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(avgCPM)}</div>
          </div>
        )}
      </div>

      {/* Placements List */}
      <div className="space-y-3">
        {result.map((placement, index) => (
          <PlacementItem key={placement.placement || index} placement={placement} />
        ))}
      </div>
    </div>
  )
}

async function downloadGoogleAdsPlacementPerformance(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsGetPlacementDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsGetPlacementDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  return `## Google Ads Placement Details\n\n\`\`\`json\n${JSON.stringify(result, null, 2)}\n\`\`\`\n\n---`
}

export const GoogleAdsGetPlacementDetailsDisplay = createWorkflowToolComponent(
  GoogleAdsGetPlacementDetailsDisplayComponent,
  downloadGoogleAdsPlacementPerformance,
  googleAdsGetPlacementDetailsToMarkdown
)
